import { createRef } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { InputRef, Table, Tag } from 'antd';
import Column from 'antd/lib/table/Column';
import { FilterDropdownProps } from 'antd/lib/table/interface';

import { Employee, Expertise, Resource } from 'models';
import {
    DateRangeFilterDropdownInstance,
    DisplayedDate,
    Edits,
    EmployeeName,
    ResourceInformation,
    TextFilterDropdownInstance,
} from 'modules/_common/components';
import {
    filterDateRange,
    getId,
    sortByAlphabet,
    sortByDate,
} from 'modules/_common/utils';
import { useAppSelector } from 'store';

type onFilterValue = string | number | boolean;

interface ICertificationsTableProps {
    onOpenModal: (expertiseToEdit: Expertise) => void;
}

export const CertificationsTable = ({
    onOpenModal,
}: ICertificationsTableProps) => {
    const isLoading = useAppSelector((state) => state.expertises.isLoading);
    const showAdminView = useAppSelector(
        (state) => state.userRoles.showAdminView
    );
    const allCertificates = useAppSelector(
        (state) => state.expertises.certificates
    );
    const filteredCertificates = useAppSelector(
        (state) => state.expertises.filteredCertificates
    );
    const inputRef = createRef<InputRef>();

    return (
        <>
            {!isLoading ? (
                <Table
                    dataSource={
                        showAdminView ? allCertificates : filteredCertificates
                    }
                    rowKey={getId}
                >
                    <Column
                        title="Id"
                        dataIndex="id"
                        sorter={(a: Expertise, b: Expertise) => a.id - b.id}
                        key="id"
                    />
                    <Column
                        title="Bezeichnung"
                        dataIndex="resource"
                        sorter={(a: Expertise, b: Expertise) =>
                            sortByAlphabet(
                                a.resource.definition,
                                b.resource.definition
                            )
                        }
                        filterDropdown={(props: FilterDropdownProps) =>
                            TextFilterDropdownInstance(
                                props,
                                'Bezeichnung',
                                inputRef
                            )
                        }
                        onFilter={(
                            value: onFilterValue,
                            expertise: Expertise
                        ) =>
                            expertise.resource.definition
                                .toLowerCase()
                                .includes(value.toString().toLowerCase())
                        }
                        render={(resource: Resource) => (
                            <ResourceInformation
                                content={resource.definition}
                            />
                        )}
                        key="resource"
                    />
                    <Column
                        title="Anbieter"
                        dataIndex="resource"
                        sorter={(a: Expertise, b: Expertise) =>
                            sortByAlphabet(a.resource.source, b.resource.source)
                        }
                        filterDropdown={(props: FilterDropdownProps) =>
                            TextFilterDropdownInstance(
                                props,
                                'Anbieter',
                                inputRef
                            )
                        }
                        onFilter={(
                            value: onFilterValue,
                            expertise: Expertise
                        ) =>
                            expertise.resource.source
                                .toLowerCase()
                                .includes(value.toString().toLowerCase())
                        }
                        render={(resource: Resource) => (
                            <ResourceInformation content={resource.source} />
                        )}
                        key="provider"
                    />
                    <Column
                        title="Ablaufdatum"
                        dataIndex="expiryDate"
                        sorter={(a: Expertise, b: Expertise) =>
                            sortByDate(a.expiryDate, b.expiryDate)
                        }
                        filterDropdown={(props: FilterDropdownProps) =>
                            DateRangeFilterDropdownInstance(props)
                        }
                        onFilter={(
                            value: onFilterValue,
                            expertise: Expertise
                        ) => {
                            const unix: Array<string> = value
                                .toString()
                                .split('-');

                            return filterDateRange(
                                new Date(parseInt(unix[0])),
                                new Date(parseInt(unix[1])),
                                expertise.expiryDate
                            );
                        }}
                        render={(date: string | null) => (
                            <DisplayedDate date={date} />
                        )}
                        key="expiryDate"
                    />
                    <Column
                        title="Mitarbeiter"
                        dataIndex="employee"
                        filterDropdown={(props: FilterDropdownProps) =>
                            TextFilterDropdownInstance(
                                props,
                                'Mitarbeiter',
                                inputRef
                            )
                        }
                        onFilter={(
                            value: onFilterValue,
                            expertise: Expertise
                        ) =>
                            `${expertise.employee.lastName}, ${expertise.employee.firstName}`
                                .toLowerCase()
                                .includes(value.toString().toLowerCase())
                        }
                        render={(employee: Employee) => (
                            <EmployeeName employee={employee} />
                        )}
                        key="employee"
                    />
                    <Column
                        title="Status"
                        dataIndex="status"
                        render={(
                            status: string | undefined,
                            expertise: Expertise
                        ) => (
                            <Tag className={expertise.statusCssClassName ?? ''}>
                                {status ?? 'Undefiniert'}
                            </Tag>
                        )}
                        key="status"
                        sorter={(a: Expertise, b: Expertise) =>
                            sortByDate(a.expiryDate, b.expiryDate)
                        }
                        filterDropdown={(props: FilterDropdownProps) =>
                            TextFilterDropdownInstance(
                                props,
                                'Status',
                                inputRef
                            )
                        }
                        onFilter={(
                            value: onFilterValue,
                            expertise: Expertise
                        ) =>
                            `${expertise.status ?? ''}`
                                .toLocaleLowerCase()
                                .includes(value.toString().toLowerCase())
                        }
                    />
                    <Column
                        render={(expertise: Expertise) => (
                            <Edits
                                type={'expertise'}
                                id={expertise.id}
                                linkText={'expertise'}
                                onOpenModal={() => onOpenModal(expertise)}
                            />
                        )}
                    />
                </Table>
            ) : (
                <LoadingOutlined />
            )}
        </>
    );
};
